import { PdBaseUI } from "../base/pd-base-ui.js";
import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-hover-box.js";
import { css, html, nothing } from "lit";
import { property } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-icon/pd-icon";
//#region src/pd-form-row/PdFormRow.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/**
* Responsive grid container for form elements.
*
* Uses CSS Grid with 12 columns and Container Queries for responsive behavior.
* Child elements use the `span` property to control their width within the grid.
* When the row width falls below 400px, all elements stack vertically.
*
* @tagname pd-form-row
* @summary 12-column grid row with Container Query support.
*
* @slot - Form elements to display in the row.
*
* @cssprop --pd-form-row-gap - Gap between elements. Default: `20px`.
* @cssprop --pd-form-row-align-items - Vertical alignment of elements. Default: `flex-start`.
* @cssprop --pd-form-row-helper-width - Width reserved for helper icon column. Default: `0`. Use `with-helper-space` attribute to enable.
* @cssprop --pd-form-row-gap-top - Top margin when `gap-top` attribute is set. Default: `1.2rem`.
*
* @attr {boolean} with-helper-space - Reserves space for helper icon column (32px). Use for consistent alignment across rows.
* @attr {boolean} no-stack - Disables responsive stacking. Elements keep their span values regardless of container width.
*
* @example
* ```html
* <pd-form-row>
*   <pd-input span="half" label="First Name"></pd-input>
*   <pd-input span="half" label="Last Name"></pd-input>
* </pd-form-row>
*
* <pd-form-row with-helper-space helper-text="Enter your full address">
*   <pd-input span="quarter" label="ZIP"></pd-input>
*   <pd-input span="three-quarters" label="City"></pd-input>
* </pd-form-row>
* ```
*/
var PdFormRow = class extends PdBaseUI {
	constructor(..._args) {
		super(..._args);
		this.helperText = "";
	}
	static {
		this.styles = [PdBaseUI.styles, css`
      :host {
        --_gap: var(--pd-form-row-gap, 20px);
        --_align: var(--pd-form-row-align-items, flex-start);
        --_helper-width: var(--pd-form-row-helper-width, 0);

        display: grid;
        grid-template-columns: repeat(12, 1fr) var(--_helper-width);
        column-gap: var(--_gap);
        row-gap: 0;
        align-items: var(--_align);
        width: 100%;
        box-sizing: border-box;
        container-type: inline-size;
        container-name: form-row;
      }

      /* Reserve space for helper icon column (for consistent alignment across rows) */
      :host([with-helper-space]) {
        --_helper-width: var(--pd-form-row-helper-width, 32px);
      }

      /* Extra top margin for visually separating sections (e.g. button rows) */
      :host([gap-top]) {
        margin-top: var(--pd-form-row-gap-top, 1.2rem);
      }

      /* Default: full width (12 columns) */
      ::slotted(*) {
        grid-column: span 12;
        /* Override fixed width from pd-input etc. to fill grid cell */
        --pd-input-field-width: 100%;
      }

      /* Semantic span values */
      ::slotted([span="quarter"]) {
        grid-column: span 3;
      }
      ::slotted([span="third"]) {
        grid-column: span 4;
      }
      ::slotted([span="half"]) {
        grid-column: span 6;
      }
      ::slotted([span="two-thirds"]) {
        grid-column: span 8;
      }
      ::slotted([span="three-quarters"]) {
        grid-column: span 9;
      }
      ::slotted([span="full"]) {
        grid-column: span 12;
      }

      /* Numeric span values */
      ::slotted([span="1"]) {
        grid-column: span 1;
      }
      ::slotted([span="2"]) {
        grid-column: span 2;
      }
      ::slotted([span="3"]) {
        grid-column: span 3;
      }
      ::slotted([span="4"]) {
        grid-column: span 4;
      }
      ::slotted([span="5"]) {
        grid-column: span 5;
      }
      ::slotted([span="6"]) {
        grid-column: span 6;
      }
      ::slotted([span="7"]) {
        grid-column: span 7;
      }
      ::slotted([span="8"]) {
        grid-column: span 8;
      }
      ::slotted([span="9"]) {
        grid-column: span 9;
      }
      ::slotted([span="10"]) {
        grid-column: span 10;
      }
      ::slotted([span="11"]) {
        grid-column: span 11;
      }
      ::slotted([span="12"]) {
        grid-column: span 12;
      }

      /* Helper column - always present for consistent alignment */
      .helper-column {
        display: flex;
        align-items: center;
        justify-content: center;
        align-self: stretch;
        min-height: 1px;
        /* Offset to align with input field (below label) */
        padding-top: 1.2em;
      }

      .help-icon {
        --pd-icon-size: 20px;
        cursor: help;
      }

      /* Responsive stacking: when row width < 400px, stack all elements vertically */
      @container form-row (max-width: 400px) {
        ::slotted(*) {
          grid-column: 1 / -2 !important;
          margin-top: 0.3em;
        }
        ::slotted(*:first-child) {
          margin-top: 0;
        }
      }

      /* Disable stacking behavior - elements keep their span values regardless of container width */
      :host([no-stack]) {
        container-type: normal;
      }
    `];
	}
	render() {
		return html`
      <slot></slot>
      <div class="helper-column">
        ${this.helperText ? html`
                <pd-hover-box>
                  <pd-icon
                    slot="normal-view"
                    icon="${pdIcons.ICON_HELP}"
                    ?activeIcon="${true}"
                    class="help-icon"
                    aria-label="Help"
                    role="img"
                  ></pd-icon>
                  <p slot="info-view">${this.helperText}</p>
                </pd-hover-box>
              ` : nothing}
      </div>
    `;
	}
};
__decorate([property({
	type: String,
	attribute: "helper-text"
})], PdFormRow.prototype, "helperText", void 0);
//#endregion
export { PdFormRow };
